import type { TradeResultSyncConfig } from 'api/elplan/elplan.types'
import { formatBidInputForDisplay } from 'utils/numberUtils/decimalInput'

export type TradeResultDatasetResolution =
  | { status: 'ok'; tradedVolumeDatasetId: number; optimizationInputDatasetId: number }
  | { status: 'misconfigured' }

export type ResolvedPositionDatasets = { positionId: string } & TradeResultDatasetResolution

/**
 * Finds the single dataset entry from a position's `datasets` array that matches the active
 * subtype. Resolution rules (note: these differ from `matchesActiveSubtype` which handles
 * bid tag matching — do not conflate the two):
 *
 * - An entry with no `metadata.subtype` is a **wildcard** and matches any active subtype
 *   (including no active subtype / empty string).
 * - An entry with `metadata.subtype` set only matches when `activeSubtype` equals that value exactly.
 * - If the number of matching entries is not exactly 1, the position is considered misconfigured
 *   (0 matches = no dataset configured for this subtype; 2+ matches = ambiguous configuration).
 *
 * Example: a position with one global entry (no subtype) works for all subtypes.
 * A position with `subtype: 'nv'` and `subtype: 'cs'` entries selects the correct one when
 * the respective subtype is active.
 */
export function resolveActiveDatasets(
  positionConfig: TradeResultSyncConfig['positions'][number],
  activeSubtype: string | undefined
): TradeResultDatasetResolution {
  const subtypeToMatch = activeSubtype ?? ''

  const matches = positionConfig.datasets.filter((entry) => {
    const entrySubtype = entry.metadata?.subtype
    if (entrySubtype === undefined) {
      // Wildcard entry: matches any active subtype value (including none)
      return true
    }
    // Typed entry: only matches the specific subtype
    return entrySubtype === subtypeToMatch
  })

  if (matches.length !== 1) {
    return { status: 'misconfigured' }
  }

  return {
    status: 'ok',
    tradedVolumeDatasetId: matches[0].traded_volume_dataset_id,
    optimizationInputDatasetId: matches[0].optimization_input_dataset_id,
  }
}

export function resolveAllPositionDatasets(
  config: TradeResultSyncConfig,
  activeSubtype: string | undefined
): ResolvedPositionDatasets[] {
  return config.positions.map((pos) => ({
    positionId: pos.position_id,
    ...resolveActiveDatasets(pos, activeSubtype),
  }))
}

/**
 * Looks up a single trade-result value for a given dataset ID and target timestamp.
 *
 * Returns the em-dash sentinel ('—') when:
 * - data is still loading,
 * - the dataset was not found in the fetched results,
 * - the target timestamp is not present in the dataset, or
 * - the value at that timestamp is null/undefined.
 */
export function lookupTradeResultValue(
  datasets: Dataset[],
  isLoading: boolean,
  datasetId: number,
  targetTime: ISODateTime
): string {
  if (isLoading) return '\u2014'
  const dataset = datasets.find((d) => d.return_id === String(datasetId))
  if (!dataset) return '\u2014'
  const timeIdx = dataset.times.findIndex((t) => t === targetTime)
  if (timeIdx === -1) return '\u2014'
  const value = dataset.values[timeIdx]
  if (value === null || value === undefined) return '\u2014'
  return formatBidInputForDisplay(String(value))
}
